import { approvedImage } from '../marketing-media';

export type ResearchMedia = {
  description: string;
  src: string;
  type: 'image';
};

export interface ResearchPaper {
  description: string;
  href: string;
  id: string;
  media: ResearchMedia;
  title: string;
}

export interface ResearchPageContent {
  contact: {
    buttonLabel: string;
    label: string;
    title: {
      emphasized: string;
      prefix: string;
      suffix: string;
    };
  };
  intro: {
    label: string;
    paragraphs: [string, string];
    title: {
      emphasized: string;
      prefix: string;
      suffix: string;
    };
  };
  measurement: {
    description: string;
    title: string;
  };
  papers: {
    description: string;
    items: ResearchPaper[];
    title: string;
  };
  scaling: {
    description: string;
    paragraphs: [string, string];
    title: string;
  };
}

export const researchPageContent = {
  intro: {
    label: 'Research',
    title: {
      prefix: 'We’re building',
      emphasized: 'kinematic world models',
      suffix: 'for the physical AI era.',
    },
    paragraphs: [
      'Cartwheel is a frontier AI research lab working on the motion layer of physical intelligence: models that represent, generate, and understand how bodies and objects move through space.',
      'The lab operates natively in 4D — not 2D video, not post-processed reconstruction. We build the models, the datasets, and the compute that make training physical AI systems tractable at scale.',
    ],
  },
  scaling: {
    title: 'The scaling law of movement',
    description: 'Motion is the next data modality to model at scale.',
    paragraphs: [
      'Every modality AI has learned to model followed the same pattern: as training data and compute scaled, model performance improved predictably — a scaling law. That pattern is why language models had their moment, then image models, then video and audio.',
      'We’ve found that motion data, and in particular human motion data, scales the same way every data modality before it has. Trained on the largest human motion corpus assembled to date, our models show a compute-optimal scaling law close to the same relationship that governs how language models scale. Motion isn’t just an underexplored modality; it’s the next one in line to scale.',
    ],
  },
  measurement: {
    title: 'Measuring what matters',
    description:
      'Scale alone isn’t enough — generated motion has to be evaluated on whether it’s physically correct and usable for action, not just visually plausible. We’re building the benchmarks to prove that: a rigorous, physics-aware standard for human motion generation the field doesn’t have yet.',
  },
  papers: {
    title: 'Our papers',
    description: 'Read our latest published research.',
    items: [
      {
        id: 'compute-optimal-scaling-laws',
        title: 'Compute-Optimal Scaling Laws for Human Motion Generation',
        href: 'https://github.com/cartwhl/scaling-laws-for-motion',
        description:
          'How motion data scales with compute and model size, and what that means for training physical AI at scale.',
        media: approvedImage(
          '01_research_our-papers_scaling-laws.png',
          'Scaling laws and motion dataset comparison from the paper.',
        ),
      },
    ],
  },
  contact: {
    label: 'Get in contact',
    title: {
      prefix: 'Ask our scientists about',
      emphasized: 'custom datasets, research opportunities, and more',
      suffix: '.',
    },
    buttonLabel: 'Talk to us',
  },
} satisfies ResearchPageContent;
